import React from 'react';
import { observer } from '../../shared/observer';
import { runBusy, isBusy } from '../../shared/busy';
import { curFormat } from '../../shared/format';

// Выбор способа получения тары (CompanyTareModes: exchange/foreign/rent/buy).
// Список режимов приходит с сервера в company_info.cart_coupled.tare_modes —
// он уже отфильтрован для клиента (пустой, пока компания не включила
// tare_modes_enabled). Ничего не хардкодим: title/hint/price_mode берём как есть,
// {{days}} в подсказке сервер разворачивает сам.
//
// Видимость гейтится родителем (см. CartApp): режимы включены и
// cart.need_coupled_count > 0. Аналог блока RecycledTare, тот же паттерн
// runBusy/isBusy + показ ошибки 422 из ответа сервера.

// Выбранный режим: подтверждённый сервером cart.tare_mode, иначе дефолт
// компании; дефолта может не быть в клиентском списке (например exchange
// выключен для клиента) — тогда падаем на первый доступный режим.
export function selectedTareMode(cart, tareInfo) {
  const modes = (tareInfo && tareInfo.tare_modes) || [];
  if (!modes.length) return null;
  const codes = modes.map((m) => m.code);
  const current = cart && cart.tare_mode;
  if (current && codes.indexOf(current) >= 0) return current;
  const def = tareInfo.tare_default_mode;
  if (def && codes.indexOf(def) >= 0) return def;
  return codes[0];
}

// «Своя тара» — тарные строки в корзину не добавляются, вместо кнопки
// «Добавить тару» показываем счётчик бутылей к обмену.
export function isOwnTareMode(code) {
  return code === 'foreign' || code === 'exchange';
}

class TareModeSelectView extends React.Component {
  constructor(props) {
    super(props);
    this.state = { busy: {}, error: null };
  }

  // Пока клиент ничего не выбирал, cart.tare_mode пуст, а радио уже показывает
  // предвыбранный дефолт компании — отправляем его на сервер сами, иначе заказ
  // уйдёт без tare_mode и без тарных строк, хотя в UI режим выглядит выбранным.
  componentDidMount() {
    if (this._autoApplied) return;
    if (this.props.cart.cart.tare_mode) return;
    const code = this.selected();
    if (!code) return;
    this._autoApplied = true;
    this.pick(code);
  }

  pick = (code) => {
    // Сравниваем с подтверждённым сервером значением, а не с this.selected():
    // тот подставляет дефолт, которого на сервере ещё нет, и клик по
    // предвыбранному пункту молча ничего бы не отправил.
    if (code === this.props.cart.cart.tare_mode) return;
    this.setState({ error: null });
    // Стор оптимистично помечает выбор и сам откатывает корзину при ошибке —
    // здесь только показываем текст ошибки сервера (422: «Режим тары недоступен»).
    runBusy(this, 'mode', () => this.props.cart.setTareMode(code))
      .catch((e) => this.setState({ error: (e && e.message) || 'Не удалось выбрать режим тары' }));
  };

  tareInfo() {
    return (this.props.auth.companyInfo.cart_coupled) || {};
  }

  selected() {
    return selectedTareMode(this.props.cart.cart, this.tareInfo());
  }

  // «Нужно тары: N» — при включённых режимах блок RecycledTare скрыт, а текст
  // потребности клиенту нужен. Подставляем полную потребность корзины
  // (associated_coupled_items_count), а не need_coupled_count: последний
  // обнуляется, как только сервер добавит строки под режим rent/buy.
  needText() {
    const need = this.props.cart.cart.associated_coupled_items_count || 0;
    if (!need) return null;
    const tpl = this.tareInfo().need_tare_txt;
    if (!tpl) return null;
    return tpl.replace('{{need_coupled_count}}', String(need));
  }

  // Подпись цены режима: free — «бесплатно», item_price — цена тарного товара.
  priceLabel(mode) {
    if (mode.price_mode !== 'item_price') return 'бесплатно';
    const item = this.tareInfo().tare_item;
    if (!item || !item.price) return null;
    const auth = this.props.auth;
    const cur = (auth.companyInfo.currency_hash && auth.companyInfo.currency_hash.symbol_html) || '';
    return `${curFormat(item.price)} ${cur}`;
  }

  render() {
    const modes = this.tareInfo().tare_modes || [];
    if (!modes.length) return null;
    const selected = this.selected();
    const busy = isBusy(this, 'mode');

    return (
      <div className={'ng-tare-modes' + (busy ? ' is-busy' : '')}>
        <p className="ng-tare-modes__title">Что делаем с тарой?</p>
        {this.needText() ? (
          <p className="ng-tare-modes__need">{this.needText()}</p>
        ) : null}
        <div className="ng-tare-modes__list">
          {modes.map((m) => (
            <label
              key={m.code}
              className={'ng-tare-mode' + (m.code === selected ? ' is-active' : '')}
            >
              <input
                type="radio"
                className="ng-tare-mode__radio"
                name="ng-tare-mode"
                value={m.code}
                checked={m.code === selected}
                disabled={busy}
                onChange={() => this.pick(m.code)}
              />
              <span className="ng-tare-mode__body">
                <span className="ng-tare-mode__head">
                  <span className="ng-tare-mode__name">{m.title}</span>
                  {this.priceLabel(m) ? (
                    <span className="ng-tare-mode__price">{this.priceLabel(m)}</span>
                  ) : null}
                </span>
                {m.hint ? <span className="ng-tare-mode__hint">{m.hint}</span> : null}
              </span>
            </label>
          ))}
        </div>
        {this.state.error ? <div className="order-message">{this.state.error}</div> : null}
      </div>
    );
  }
}

export const TareModeSelect = observer(TareModeSelectView, ['cartStore', 'authStore']);



// WEBPACK FOOTER //
// ./app/javascript/website_newgen/js/components/Cart/TareModeSelect.jsx